import { Component, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PrestamosService } from '../../../../../modules/finanzas/prestamos/prestamos.service';
import { PrestamosModel } from '../../../../../modules/finanzas/prestamos/prestamos.model';
import { AccountMovementsViewModel } from '../../../../../modules/accounting/models/accounting-movement.model';
import { AddFileService } from '../../../../../shared/components/add-file/add-file.service';
import { LocalDB } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { NotificationService } from '../../../../shared/services/notification.service';
import { LoanAttachmentDialogData, loanHasFile } from '../../models/loan.model';

@Component({
  selector: 'app-loan-attachment-dialog',
  templateUrl: './loan-attachment-dialog.component.html',
  styleUrls: ['./loan-attachment-dialog.component.scss'],
  standalone: false,
})
export class LoanAttachmentDialogComponent {
  readonly originEntity = 'Finanzas';

  selectedFile: File | null = null;
  saving = false;
  saveError = '';
  submitted = false;

  static open(dialog: MatDialog, data: LoanAttachmentDialogData): Observable<boolean> {
    return dialog.open<LoanAttachmentDialogComponent, LoanAttachmentDialogData, boolean>(LoanAttachmentDialogComponent, {
      width: '560px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private dialogRef: MatDialogRef<LoanAttachmentDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: LoanAttachmentDialogData,
    private prestamosService: PrestamosService,
    private addFileService: AddFileService,
    private notify: NotificationService,
  ) { }

  get subtitle(): string {
    return [this.data.loan.entidad, this.data.loan.numero ? 'Nº ' + this.data.loan.numero : null].filter(Boolean).join(' · ');
  }

  get hasCurrentFile(): boolean {
    return loanHasFile(this.data.loan);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    this.saveError = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.selectedFile = null;
      this.saveError = 'El cuadro de amortización debe ser un Excel (.xlsx).';
      input.value = '';
      return;
    }
    this.selectedFile = file;
  }

  clearFile(input: HTMLInputElement): void {
    this.selectedFile = null;
    this.saveError = '';
    input.value = '';
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    if (!this.selectedFile) return;

    this.saving = true;
    this.saveError = '';
    this.dialogRef.disableClose = true;

    const formData = new FormData();
    formData.append('file', this.selectedFile, this.selectedFile.name);

    this.addFileService.attachFile(formData, this.originEntity)
      .then(response => {
        if (!response || response.error) {
          throw { error: { message: response && response.message } };
        }
        LocalDB.RemoveForFiles();
        return this.prestamosService.editFileAttachment(this.buildPayload(response.uid));
      })
      .then(ok => {
        if (ok !== true) throw new Error();
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.notify.ok('Se ha adjuntado el cuadro de amortización correctamente');
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        const serverMessage = error && error.error && error.error.message;
        this.saveError = serverMessage || 'No se ha podido adjuntar el fichero. Inténtalo de nuevo.';
      });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private buildPayload(fileUid: string): PrestamosModel {
    return {
      carencia: 0,
      bankAccountsUid: consts.EMPTY_GUID,
      accountingModelsCortoPlazoNumber: '',
      accountingModelsLargoPlazoNumber: '',
      numero: '',
      entidad: '',
      importeInicial: 0,
      importePendiente: 0,
      fechaConstitucion: new Date(),
      fechaFinalizacion: new Date(),
      plazo: 0,
      tipoInteres: 0,
      uid: this.data.loan.uid,
      tipoLiquidaciones: 0,
      filesPrestamosUid: fileUid,
      duracion: 0,
      comisionApertura: 0,
      manualMovement: new AccountMovementsViewModel()
    };
  }
}
